import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { apiClient } from '../../lib/api-client'
import { useRegenerateBrief } from '../../hooks/useCompetitiveData'
import type { BriefData, BriefResponse } from '../../hooks/useCompetitiveData'
import KeywordAlertPanel from './KeywordAlertPanel'
import ThreatCard from './ThreatCard'

interface IntelligenceBriefBannerProps {
  brief: BriefData
  generatedAt: string | null
  showThreatCards?: boolean
  showKeywordAlerts?: boolean
}

const BRIEF_REGEN_POLL_INTERVAL_MS = 10_000
const BRIEF_REGEN_MAX_POLLS = 60

function formatGeneratedAt(value: string | null): string {
  if (!value) {
    return 'recently'
  }

  const timestamp = new Date(value)
  if (Number.isNaN(timestamp.getTime())) {
    return 'recently'
  }

  return timestamp.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getBriefAgeInfo(generatedAt: string | null): { isStale: boolean; staleDays: number } {
  if (!generatedAt) {
    return { isStale: false, staleDays: 0 }
  }

  const generatedAtMs = new Date(generatedAt).getTime()
  if (!Number.isFinite(generatedAtMs)) {
    return { isStale: false, staleDays: 0 }
  }

  const ageMs = Date.now() - generatedAtMs
  const staleDays = Math.floor(ageMs / (24 * 60 * 60 * 1000))
  return {
    isStale: ageMs > 24 * 60 * 60 * 1000,
    staleDays,
  }
}

export default function IntelligenceBriefBanner({
  brief,
  generatedAt,
  showThreatCards = true,
  showKeywordAlerts = true,
}: IntelligenceBriefBannerProps) {
  const queryClient = useQueryClient()
  const [regenerateHint, setRegenerateHint] = useState<string | null>(null)
  const [regenerateError, setRegenerateError] = useState<string | null>(null)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const pollIntervalRef = useRef<ReturnType<typeof window.setInterval> | null>(null)
  const pollCountRef = useRef(0)
  const triggerTimeRef = useRef<number | null>(null)
  const { mutateAsync: regenerateBrief } = useRegenerateBrief()
  const { isStale, staleDays } = useMemo(() => getBriefAgeInfo(generatedAt), [generatedAt])

  const stopPolling = () => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    pollCountRef.current = 0
    triggerTimeRef.current = null
  }

  const checkForUpdatedBrief = async (): Promise<boolean> => {
    if (pollCountRef.current >= BRIEF_REGEN_MAX_POLLS) {
      stopPolling()
      setIsRegenerating(false)
      setRegenerateError('Regeneration is taking longer than expected. Check back in a few minutes.')
      return true
    }

    pollCountRef.current += 1

    try {
      const latestBrief = await apiClient.get<BriefResponse>('/competitive/briefs')
      const generatedAtValue = latestBrief.generated_at ?? latestBrief.brief?.generated_at ?? null
      const generatedAtMs = generatedAtValue ? new Date(generatedAtValue).getTime() : Number.NaN
      const triggerTime = triggerTimeRef.current

      if (
        triggerTime !== null &&
        Number.isFinite(generatedAtMs) &&
        generatedAtMs > triggerTime
      ) {
        stopPolling()
        setIsRegenerating(false)
        setRegenerateError(null)
        setRegenerateHint('Brief refreshed with latest data.')
        queryClient.setQueryData<BriefResponse>(['brief', 'latest'], latestBrief)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['brief'] }),
          queryClient.invalidateQueries({ queryKey: ['competitive', 'dashboard'] }),
        ])
        return true
      }
    } catch (error) {
      console.error('[IntelligenceBriefBanner] brief regen poll failed:', error)
    }

    return false
  }

  const handleRegenerate = async () => {
    stopPolling()
    setRegenerateHint(null)
    setRegenerateError(null)
    setIsRegenerating(true)

    try {
      await regenerateBrief()

      triggerTimeRef.current = Date.now()
      pollCountRef.current = 0

      const completedImmediately = await checkForUpdatedBrief()
      if (!completedImmediately) {
        pollIntervalRef.current = window.setInterval(() => {
          void checkForUpdatedBrief()
        }, BRIEF_REGEN_POLL_INTERVAL_MS)
      }
    } catch {
      stopPolling()
      setIsRegenerating(false)
      setRegenerateError('Failed to queue regeneration. Try again.')
    }
  }

  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [])

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 bg-[var(--patterniq-navy-900)] px-5 py-4 text-white">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-200">Executive Intelligence Brief</p>
          <h2 className="mt-1 text-lg font-bold">Today&apos;s Competitive Priorities</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-300">
            <p>Generated {formatGeneratedAt(generatedAt)}</p>
            <button
              type="button"
              onClick={() => void handleRegenerate()}
              disabled={isRegenerating}
              className="rounded border border-slate-500/50 px-1.5 py-0.5 text-xs text-slate-200 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isRegenerating ? '↻ Regenerating…' : '↻ Regenerate'}
            </button>
          </div>
          {isRegenerating && (
            <p className="mt-1 flex items-center gap-2 text-xs text-slate-300">
              <span className="h-3 w-3 animate-spin rounded-full border border-slate-300 border-t-transparent" />
              Brief regeneration queued — checking every 10s…
            </p>
          )}
          {regenerateHint && <p className="mt-1 text-xs text-slate-300">{regenerateHint}</p>}
          {regenerateError && <p className="mt-1 text-xs text-red-300">{regenerateError}</p>}
        </div>
        <ShieldAlert className="mt-1 h-5 w-5 shrink-0 text-red-300" />
      </div>

      <div className="space-y-4 p-5">
        {isStale && (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span>⚠</span>
            <span>
              This brief is {staleDays} day{staleDays !== 1 ? 's' : ''} old. Data may be stale.
            </span>
          </div>
        )}

        <p className="competitive-body-text rounded-lg bg-slate-50 p-3">{brief.executive_summary}</p>

        {showThreatCards && (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            {brief.top_threats.length === 0 ? (
              <div className="xl:col-span-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No urgent threat cards in today&apos;s brief.
              </div>
            ) : (
              brief.top_threats.map((threat) => (
                <ThreatCard key={`${threat.competitor_id}-${threat.headline}`} threat={threat} />
              ))
            )}
          </div>
        )}

        {showKeywordAlerts && <KeywordAlertPanel alerts={brief.keyword_alerts} />}
      </div>
    </section>
  )
}
